'use client'

import { useEffect, useRef } from 'react'
import anime from 'animejs'

interface SectionPromptLoaderProps {
  isLoading: boolean
}

const SectionPromptLoader: React.FC<SectionPromptLoaderProps> = ({ isLoading }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  
  useEffect(() => {
    if (!containerRef.current || !isLoading) return
    
    containerRef.current.innerHTML = ''
    
    const mainContainer = document.createElement('div')
    mainContainer.className = 'relative w-full h-full min-h-[300px] flex flex-col items-center'
    containerRef.current.appendChild(mainContainer)
    
    // Create logo and title container
    const titleContainer = document.createElement('div')
    titleContainer.className = 'flex items-center gap-2 mb-6 mt-6'
    mainContainer.appendChild(titleContainer)
    
    // Create animated logo
    const logoImg = document.createElement('img')
    logoImg.src = '/images/waltz-logo-orange-black.svg'
    logoImg.alt = 'Waltz AI'
    logoImg.width = 150
    logoImg.height = 50
    logoImg.className = 'mb-2'
    titleContainer.appendChild(logoImg)

    // Animate logo pulse
    anime({
    targets: logoImg,
    scale: [1, 1.05, 1],
    opacity: [1, 0.9, 1],
    duration: 2000,
    easing: 'easeInOutQuad',
    loop: true
    })
    
    // Create title
    // const title = document.createElement('div')
    // title.className = 'text-[#FF6600] font-bold text-xl'
    // title.textContent = 'Waltz AI'
    // titleContainer.appendChild(title)
    
    // Create subtitle
    const subtitle = document.createElement('div')
    subtitle.className = 'text-gray-600 text-sm mb-8'
    subtitle.textContent = 'Building your digital experience...'
    mainContainer.appendChild(subtitle)
    
    // Create brain visualization container
    const brainContainer = document.createElement('div')
    brainContainer.className = 'relative w-[160px] h-[120px] mb-6'
    mainContainer.appendChild(brainContainer)
    
    // Create brain hemispheres
    const leftHemisphere = document.createElement('div')
    leftHemisphere.className = 'absolute left-0 top-0 w-[70px] h-[100px] rounded-l-full opacity-70'
    leftHemisphere.style.backgroundColor = '#FF7733' // Lighter shade
    brainContainer.appendChild(leftHemisphere)
    
    const rightHemisphere = document.createElement('div')
    rightHemisphere.className = 'absolute right-0 top-0 w-[70px] h-[100px] rounded-r-full opacity-70'
    rightHemisphere.style.backgroundColor = '#E65C00' // Darker shade
    brainContainer.appendChild(rightHemisphere)
    
    // Create neural connections
    for (let i = 0; i < 12; i++) {
      const connection = document.createElement('div')
      connection.className = 'absolute rounded-full'
      connection.style.backgroundColor = '#FF8C4D' // Even lighter shade
      connection.style.width = '4px'
      connection.style.height = '4px'
      connection.style.left = `${40 + Math.random() * 80}px`
      connection.style.top = `${20 + Math.random() * 80}px`
      connection.style.opacity = '0'
      brainContainer.appendChild(connection)
      
      // Animate neural connections
      anime({
        targets: connection,
        opacity: [0, 0.8, 0],
        scale: [0.5, 1.5, 0.5],
        translateX: () => anime.random(-15, 15) + 'px',
        translateY: () => anime.random(-15, 15) + 'px',
        duration: () => anime.random(1200, 2800),
        delay: anime.random(0, 1000),
        loop: true,
        easing: 'easeInOutQuad'
      })
    }
    
    // Animate brain hemispheres
    anime({
      targets: leftHemisphere,
      translateX: [-5, 0, -5],
      opacity: [0.6, 0.8, 0.6],
      duration: 3000,
      loop: true,
      easing: 'easeInOutQuad'
    })
    
    anime({
      targets: rightHemisphere,
      translateX: [5, 0, 5],
      opacity: [0.6, 0.8, 0.6],
      duration: 3000,
      loop: true,
      easing: 'easeInOutQuad'
    })
    
    // Create messages container
    const messageContainer = document.createElement('div')
    messageContainer.className = 'w-[90%] max-w-[320px] text-center text-gray-700 mt-4'
    mainContainer.appendChild(messageContainer)
    
    const messages = [
      "Analyzing your design preferences",
      "Crafting your unique sections",
      "Orchestrating content strategy",
      "Building your digital presence",
      "Composing a harmonious layout",
      "Organizing information architecture",
      "Creating a perfect visual flow",
      "Arranging content elements",
      "Planning your user journey"
    ]
    
    // Rotate through messages
    let currentMessageIndex = 0
    messageContainer.textContent = messages[0]
    
    const messageInterval = setInterval(() => {
      currentMessageIndex = (currentMessageIndex + 1) % messages.length
      
      // Fade out current message
      anime({
        targets: messageContainer,
        opacity: [1, 0],
        duration: 400,
        easing: 'easeOutQuad',
        complete: () => {
          // Change message and fade in
          messageContainer.textContent = messages[currentMessageIndex]
          anime({
            targets: messageContainer,
            opacity: [0, 1],
            duration: 400,
            easing: 'easeInQuad'
          })
        }
      })
    }, 3000)
    
    // Create section blocks visualization
    const blocksContainer = document.createElement('div')
    blocksContainer.className = 'relative w-[90%] max-w-[320px] h-[80px] mt-8'
    mainContainer.appendChild(blocksContainer)
    
    // Add progress indicator
    const progressContainer = document.createElement('div')
    progressContainer.className = 'w-full h-1 bg-gray-200 rounded-full overflow-hidden mt-4'
    mainContainer.appendChild(progressContainer)
    
    const progressBar = document.createElement('div')
    progressBar.className = 'h-full rounded-full'
    progressBar.style.background = 'linear-gradient(to right, #FF6600, #FF8C4D)'
    progressBar.style.width = '0%'
    progressContainer.appendChild(progressBar)
    
    // Animate progress bar
    anime({
      targets: progressBar,
      width: ['0%', '90%'],
      duration: 15000,
      easing: 'easeInOutQuad'
    })
    
    // Create section blocks that appear and stack
    const createSection = (index: number) => {
      const blockWidth = ['100%', '80%', '60%'][index % 3]
      const blockColor = [
        'rgba(255, 102, 0, 0.7)',  // #FF6600
        'rgba(230, 92, 0, 0.7)',   // #E65C00
        'rgba(255, 119, 51, 0.7)'  // #FF7733
      ][index % 3]
      
      const block = document.createElement('div')
      block.className = 'absolute left-0 rounded-md'
      block.style.width = blockWidth
      block.style.height = '20px'
      block.style.opacity = '0'
      block.style.transform = 'translateY(20px)'
      block.style.left = index % 2 === 0 ? '0' : 'auto'
      block.style.right = index % 2 === 0 ? 'auto' : '0'
      block.style.backgroundColor = blockColor
      block.style.top = `${index * 25}px`
      
      blocksContainer.appendChild(block)
      
      // Animate block entrance
      anime({
        targets: block,
        translateY: [20, 0],
        opacity: [0, 1],
        delay: index * 300,
        duration: 800,
        easing: 'easeOutQuad'
      })
    }
    
    // Create initial blocks
    for (let i = 0; i < 3; i++) {
      createSection(i)
    }
    
    return () => {
      clearInterval(messageInterval)
      anime.remove('*')
    }
  }, [isLoading])
  
  if (!isLoading) return null
  
  return (
    <div 
      ref={containerRef} 
        className="fixed left-1/2 -translate-x-1/2 md:translate-x-[20%] top-1/2 -translate-y-1/2 w-[90%] sm:w-[400px] md:w-[450px] h-full max-h-[500px] bg-white overflow-hidden flex items-center justify-center rounded-lg shadow-md z-50"
    />
  )
}

export default SectionPromptLoader